import React, { useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import { ModalHeader } from "components/Shared";
import { AutoPopulateDictionary } from "actions/conditions";

// Fixed sections have static headers. Per-tenancy sections are DYNAMIC: the
// server's auto_populate_dictionary emits one group per tenancy — group
// "tenancy_<n>" with a `group_label` like "TENANCY 1 (Shop A1)" — so their
// headers come from the data, not this map. Tenancy sections are ordered between
// AMENDMENT and COMPANY (see orderedGroups below).
//
// There is no LEASE section any more. Its fields read off TCP's own legacy
// Lease record, which a project created by /lease_data does not have, so the
// whole section resolved blank on an amendment-backed project. The terms come
// from LEASE AGREEMENT (TCode, type, status) and AMENDMENT (every commercial
// field) instead.
const STATIC_BEFORE = ["project", "tenant", "lease_agreement", "amendment"];
const STATIC_AFTER = ["company", "property"];
const GROUP_LABELS: Record<string, string> = {
  project: "PROJECT",
  tenant: "TENANT",
  lease_agreement: "LEASE AGREEMENT",
  amendment: "AMENDMENT",
  company: "COMPANY",
  property: "PROPERTY",
};

// Per-tenancy group keys look like "tenancy_1", "tenancy_2", …
const TENANCY_GROUP = /^tenancy_(\d+)$/;

interface AvailableFieldsModalProps {
  isOpen: boolean;
  dictionary: AutoPopulateDictionary;
  onClose: () => void;
  onInsert: (token: string) => void;
}

const AvailableFieldsModal: React.FC<AvailableFieldsModalProps> = ({
  isOpen,
  dictionary,
  onClose,
  onInsert,
}) => {
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  // Reset the search whenever the modal opens so each field starts fresh
  useEffect(() => {
    if (isOpen) setSearch("");
  }, [isOpen]);

  const entries = Object.entries(dictionary || {});
  const lowerSearch = search.toLowerCase();

  const filteredEntries = lowerSearch
    ? entries.filter(([key, entry]) => {
        return (
          entry.label.toLowerCase().includes(lowerSearch) ||
          key.toLowerCase().includes(lowerSearch) ||
          // Let users find a tenancy section by its header, e.g. typing the
          // tenancy's name ("TENANCY 1 (Shop A1)").
          (entry.group_label || "").toLowerCase().includes(lowerSearch)
        );
      })
    : entries;

  const byGroup = (group: string) =>
    filteredEntries.filter(([, entry]) => entry && entry.group === group);

  const hasAnyResults = filteredEntries.length > 0;

  // Groups present in the (filtered) results, ordered: fixed "before" groups,
  // then per-tenancy groups (numeric order), then fixed "after" groups. Any
  // unrecognised group is appended last so nothing silently disappears.
  const presentGroups: string[] = [];
  filteredEntries.forEach(([, entry]) => {
    if (entry && !presentGroups.includes(entry.group)) presentGroups.push(entry.group);
  });
  const tenancyGroups = presentGroups
    .filter((g) => TENANCY_GROUP.test(g))
    .sort((a, b) => Number(a.match(TENANCY_GROUP)![1]) - Number(b.match(TENANCY_GROUP)![1]));
  const orderedGroups = [...STATIC_BEFORE, ...tenancyGroups, ...STATIC_AFTER].filter((g) =>
    presentGroups.includes(g)
  );
  presentGroups.forEach((g) => {
    if (!orderedGroups.includes(g)) orderedGroups.push(g);
  });

  // Section header: prefer the data-supplied group_label (dynamic sections),
  // then the static map, then a sensible uppercase fallback.
  const headerFor = (group: string): string => {
    const withLabel = filteredEntries.find(([, e]) => e && e.group === group && e.group_label);
    return (withLabel && withLabel[1].group_label!) || GROUP_LABELS[group] || group.toUpperCase();
  };

  const handleRowClick = (key: string) => {
    const token = `{% ${key} %}`;
    onInsert(token);
    // onInsert closes the modal via parent setting openForKey=null
  };

  return (
    // react-modal so the chrome (overlay, centering, header/section padding)
    // comes from the shared _modal.scss rules — same look as the template
    // condition modal. Escape and overlay clicks close via onRequestClose.
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      onAfterOpen={() => searchRef.current?.focus()}
      className="available-fields-modal"
      contentLabel="Insert a field"
      ariaHideApp={false}
    >
      <ModalHeader title="Insert a field" onRequestClose={onClose} />
      <section>
        <p className="text-muted" style={{ fontSize: 13 }}>
          Click a field to drop its placeholder into the text box.
        </p>

        {/* Search */}
        <div style={{ position: "relative", marginBottom: 8 }}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
            }}
          >
            <circle cx="9" cy="9" r="7" stroke="#9CA3AF" strokeWidth="2" />
            <line x1="14.5" y1="14.5" x2="18.5" y2="18.5" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            ref={searchRef}
            type="text"
            className="form-control"
            placeholder="Search fields…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ paddingLeft: 34 }}
            aria-label="Search fields"
          />
        </div>

        {/* Grouped list */}
        {entries.length === 0 && (
          <div className="text-muted py-3" style={{ fontSize: 14 }}>
            No fields available for this condition.
          </div>
        )}
        {entries.length > 0 && !hasAnyResults && (
          <div className="text-muted py-3" style={{ fontSize: 14 }}>
            No fields match your search.
          </div>
        )}
        {entries.length > 0 &&
          orderedGroups.map((group) => {
            const rows = byGroup(group);
            if (rows.length === 0) return null;
            return (
              <div key={group}>
                <div
                  className="text-muted text-uppercase pt-3 pb-1"
                  style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em" }}
                >
                  {headerFor(group)}
                </div>
                {rows.map(([key, entry]) => {
                  const token = `{% ${key} %}`;
                  const hasValue = entry.value !== null && entry.value !== "";
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleRowClick(key)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        background: "transparent",
                        border: "none",
                        borderBottom: "1px solid #f3f4f6",
                        padding: "8px 4px",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.background = "#f8f9fa";
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                      }}
                    >
                      <div style={{ minWidth: 0, flex: 1, marginRight: 12 }}>
                        <div style={{ fontSize: 13, fontWeight: 400, color: "#212529" }}>
                          {entry.label}
                        </div>
                        <div
                          style={{
                            fontFamily: "monospace",
                            fontSize: 12,
                            color: "#2563EB",
                            marginTop: 2,
                          }}
                        >
                          {token}
                        </div>
                      </div>
                      <div style={{ fontSize: 13, flexShrink: 0, textAlign: "right", maxWidth: 180 }}>
                        {hasValue ? (
                          <span style={{ color: "#374151" }}>{entry.value}</span>
                        ) : (
                          <em className="text-muted">no value yet</em>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })}
      </section>
    </Modal>
  );
};

export default AvailableFieldsModal;
